/**
 * NOTE: Weight functionality is removed for now.
 * This store will be implemented in a future sprint.
 * 
 * This file is kept as a placeholder to avoid breaking imports.
 */

import { writable, get } from 'svelte/store';
import type { QuestionValue } from '@lib/types/survey';

// Types for the weights functionality
export interface AxisDefinition {
  id: string;
  min: number;
  max: number;
  normalizedThresholds: {
    low: number;
    neutral: number;
    high: number;
  };
}

export interface WeightEntry {
  key: string;
  axes: Record<string, number>;
}

export interface ProfileDefinition {
  id: string;
  label: string;
  description: string;
  longDescription: string;
  conditions: ProfileCondition[];
}

export interface ProfileCondition {
  axis: string;
  min?: number;
  max?: number;
}

// Store question contributions individually
interface QuestionContribution {
  questionId: string;
  response: QuestionValue;
  contributions: Record<string, number>; // axis -> value
}

export interface WeightsState {
  // Raw scores for each axis
  scores: Record<string, number>;
  // Normalized scores (0-100) for each axis
  normalizedScores: Record<string, number>;
  // Axis definitions from survey config
  axes: AxisDefinition[];
  // Profile definitions from survey config
  profiles: ProfileDefinition[];
  // The assigned profile ID based on scores
  assignedProfileId: string | null;
  // Track contributions by question to prevent double-counting
  questionContributions: QuestionContribution[];
}

// Constants
const WEIGHTS_STORAGE_KEY = 'walku:weights';

// Helper to get weights state from sessionStorage
function getStoredWeightsState(): WeightsState | null {
  if (typeof window === 'undefined') return null;
  
  const storedState = sessionStorage.getItem(WEIGHTS_STORAGE_KEY);
  
  if (!storedState) return null;
  
  try {
    const parsedState = JSON.parse(storedState);
    return parsedState;
  } catch (e) {
    return null;
  }
}

// Helper to save weights state to sessionStorage
function saveWeightsState(state: WeightsState): void {
  if (typeof window === 'undefined') return;
  sessionStorage.setItem(WEIGHTS_STORAGE_KEY, JSON.stringify(state));
}

// Create initial state for weights store
function createInitialState(axes: AxisDefinition[], profiles: ProfileDefinition[]): WeightsState {
  // Initialize scores for each axis to 0
  const scores: Record<string, number> = {};
  const normalizedScores: Record<string, number> = {};
  
  axes.forEach(axis => {
    scores[axis.id] = 0;
    normalizedScores[axis.id] = 50; // Default to middle point
  });
  
  return {
    scores,
    normalizedScores,
    axes,
    profiles,
    assignedProfileId: null,
    questionContributions: []
  };
}

// Calculate total scores from individual question contributions
function calculateTotalScores(contributions: QuestionContribution[]): Record<string, number> {
  const totalScores: Record<string, number> = {};
  
  // Sum up contributions from all questions
  contributions.forEach(contribution => {
    Object.entries(contribution.contributions).forEach(([axisId, value]) => {
      totalScores[axisId] = (totalScores[axisId] || 0) + value;
    });
  });
  
  return totalScores;
}

// Helper to normalize scores to 0-100 scale
function normalizeScores(
  scores: Record<string, number>,
  axes: AxisDefinition[],
  contributions: QuestionContribution[]
): Record<string, number> {
  const normalizedScores: Record<string, number> = {};

  // Count how many questions contributed to each axis
  const questionCountPerAxis: Record<string, number> = {};
  contributions.forEach(contribution => {
    Object.keys(contribution.contributions).forEach(axisId => {
      questionCountPerAxis[axisId] = (questionCountPerAxis[axisId] || 0) + 1;
    });
  });

  axes.forEach(axis => {
    const score = scores[axis.id] || 0;
    const { min, max } = axis;
    const questionCount = questionCountPerAxis[axis.id];

    // If no questions have contributed to this axis, keep it at neutral (50)
    if (!questionCount || questionCount === 0) {
      normalizedScores[axis.id] = 50;
      return;
    }

    // Calculate actual min/max based on number of contributing questions
    // If axis is -10 to 10 per question, and 6 questions contribute,
    // actual range is -60 to 60
    const actualMin = min * questionCount;
    const actualMax = max * questionCount;
    const actualRange = actualMax - actualMin;

    // Normalize to 0-100 scale using the actual range:
    // normalizedScore = ((score - actualMin) / actualRange) * 100
    // Example: raw score 30, axis -10 to 10, 6 questions:
    // actualMin = -60, actualMax = 60, actualRange = 120
    // (30 - (-60)) / 120 * 100 = 90/120 * 100 = 75
    const normalizedScore = Math.round(((score - actualMin) / actualRange) * 100);

    // Ensure value is within 0-100 bounds
    const boundedScore = Math.max(0, Math.min(100, normalizedScore));
    normalizedScores[axis.id] = boundedScore;
  });

  return normalizedScores;
}

// Helper to determine assigned profile
function determineProfile(normalizedScores: Record<string, number>, profiles: ProfileDefinition[]): string | null {
  // Check each profile to see if conditions are met
  for (const profile of profiles) {
    // If a profile has no conditions, skip it for now (it's our fallback)
    if (!profile.conditions || profile.conditions.length === 0) {
      continue;
    }
    
    let conditionsMet = true;
    
    for (const condition of profile.conditions) {
      const score = normalizedScores[condition.axis] || 50;
      
      if (condition.min !== undefined && score < condition.min) {
        conditionsMet = false;
        break;
      }
      
      if (condition.max !== undefined && score > condition.max) {
        conditionsMet = false;
        break;
      }
    }
    
    if (conditionsMet) {
      return profile.id;
    }
  }
  
  // If no profile matches, look for a default profile with no conditions
  const defaultProfile = profiles.find(p => !p.conditions || p.conditions.length === 0);
  if (defaultProfile) {
    return defaultProfile.id;
  }
  
  return null;
}

// Create the weights store
function createWeightsStore() {
  const initialState = getStoredWeightsState();
  const { subscribe, update, set } = writable<WeightsState | null>(initialState);
  
  return {
    subscribe,
    
    // Initialize the weights store with axes and profiles
    initialize: (axes: AxisDefinition[], profiles: ProfileDefinition[]) => {
      // Check if there is existing state in the store
      const currentState = get({ subscribe });
      
      // If we have existing state, verify that the axes match
      // If axes don't match, we need to reset the state
      let shouldReset = false;
      
      if (currentState) {
        // Check if axes property exists (backwards compatibility)
        if (!currentState.axes || !Array.isArray(currentState.axes)) {
          shouldReset = true;
        } else if (currentState.axes.length !== axes.length) {
          shouldReset = true;
        } else {
          // Check if the axis IDs match
          const currentAxisIds = currentState.axes.map(a => a.id).sort();
          const newAxisIds = axes.map(a => a.id).sort();

          if (JSON.stringify(currentAxisIds) !== JSON.stringify(newAxisIds)) {
            shouldReset = true;
          }
        }
      }
      
      // Create new initial state if needed
      if (!currentState || shouldReset) {
        const initialState = createInitialState(axes, profiles);
        
        // Determine a default profile on initialization, even with all scores at 0
        // This ensures a profile is assigned immediately on survey load
        initialState.assignedProfileId = determineProfile(initialState.normalizedScores, profiles);
        
        set(initialState);
        saveWeightsState(initialState);
      } else {
        // Update axes and profiles in the existing state
        update(state => {
          if (!state) {
            // If somehow state is null, create a new initial state
            return createInitialState(axes, profiles);
          }
          
          const newState: WeightsState = {
            ...state,
            axes,
            profiles
          };
          
          // Re-determine profile if we don't have one already
          if (!newState.assignedProfileId) {
            newState.assignedProfileId = determineProfile(newState.normalizedScores, profiles);
          }
          
          saveWeightsState(newState);
          return newState;
        });
      }
    },
    
    // Add weights for a question response
    addWeights: (questionId: string, response: QuestionValue, weights: WeightEntry[]) => {
      // Get the current state
      const state = get({ subscribe });
      
      if (!state) {
        return;
      }
      
      // Determine which weights apply to this response
      const contributions: Record<string, number> = {};
      
      if (Array.isArray(response)) {
        // Handle array responses (multiple selections)
        response.forEach(selectedKey => {
          const selectedKeyString = String(selectedKey);
          const weight = weights.find(w => String(w.key) === selectedKeyString);
          
          if (weight) {
            if (weight.axes && typeof weight.axes === 'object') {
              // Add contributions for each axis
              Object.entries(weight.axes).forEach(([axisId, value]) => {
                const numValue = typeof value === 'number' ? value : 0;
                contributions[axisId] = (contributions[axisId] || 0) + numValue;
              });
            }
          }
        });
      } else {
        // Handle single response
        const responseString = String(response);
        const weight = weights.find(w => String(w.key) === responseString);
        
        if (weight) {
          if (weight.axes && typeof weight.axes === 'object') {
            // Add contributions for each axis
            Object.entries(weight.axes).forEach(([axisId, value]) => {
              const numValue = typeof value === 'number' ? value : 0;
              contributions[axisId] = (contributions[axisId] || 0) + numValue;
            });
          }
        }
      }
      
      // Update the store with the new contributions
      update(state => {
        if (!state) {
          return null;
        }
        
        // Check if we already have a contribution for this question
        const existingContributionIndex = state.questionContributions.findIndex(
          c => c.questionId === questionId
        );
        
        let newContributions = [...state.questionContributions];
        
        if (existingContributionIndex >= 0) {
          // Update existing contribution
          newContributions[existingContributionIndex] = {
            questionId,
            response,
            contributions
          };
        } else {
          // Add new contribution
          newContributions.push({
            questionId,
            response,
            contributions
          });
        }
        
        // Calculate total scores
        const newScores = calculateTotalScores(newContributions);

        // Normalize scores to 0-100 scale (pass contributions to count questions per axis)
        const normalizedScores = normalizeScores(newScores, state.axes, newContributions);

        // Determine assigned profile
        const assignedProfileId = determineProfile(normalizedScores, state.profiles);
        
        // Create the new state
        const newState: WeightsState = {
          ...state,
          scores: newScores,
          normalizedScores,
          assignedProfileId,
          questionContributions: newContributions
        };
        
        // Save to sessionStorage
        saveWeightsState(newState);
        
        return newState;
      });
    },
    
    // Get the current scores (raw)
    getScores: () => {
      const state = get({ subscribe });
      return state ? state.scores : {};
    },
    
    // Get the normalized scores (0-100)
    getNormalizedScores: () => {
      const state = get({ subscribe });
      return state ? state.normalizedScores : {};
    },
    
    // Get the assigned profile
    getProfile: () => {
      const state = get({ subscribe });
      if (!state || !state.assignedProfileId) return null;
      
      // Find the profile by ID
      return state.profiles.find(p => p.id === state.assignedProfileId) || null;
    },
    
    // Reset the store
    reset: () => {
      // Get the current state to preserve axes and profiles
      const state = get({ subscribe });
      
      if (state) {
        const initialState = createInitialState(state.axes, state.profiles);
        set(initialState);
        saveWeightsState(initialState);
      }
    }
  };
}

// Create and export the weights store
export const weightsStore = createWeightsStore();

// Listen for survey identity changes and reset store
if (typeof window !== 'undefined') {
  window.addEventListener('walku:clear-stores', () => {
    weightsStore.reset();
  });
}

// Export empty types to avoid breaking imports
export interface WeightRule {}
export interface WeightCategory {} 